import { Link } from 'react-router-dom';

const VARIANTS = {
  primary: 'bg-amber text-deep hover:shadow-[0_10px_24px_rgba(232,163,61,0.35)]',
  outline: 'bg-transparent text-white border border-white/50 hover:border-white',
  dark: 'bg-deep text-white hover:shadow-[0_10px_24px_rgba(18,46,27,0.35)]',
  white: 'bg-white text-deep hover:shadow-[0_10px_24px_rgba(0,0,0,0.15)]',
};

const SIZES = {
  md: 'px-6 py-3.5 text-[14.5px]',
  sm: 'px-5 py-2.5 text-[13.5px]',
};

/**
 * Renders as <Link> for internal routes, <a> for external/anchor targets,
 * and <button> when given onClick instead of a destination — so the correct
 * element is always used rather than a div with a click handler.
 */
export default function Button({
  to,
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...props
}) {
  const classes = [
    'inline-flex items-center justify-center gap-2 rounded-full font-semibold',
    'transition-all duration-150 hover:-translate-y-px',
    VARIANTS[variant] ?? VARIANTS.primary,
    SIZES[size] ?? SIZES.md,
    className,
  ].join(' ');

  /*
   * Only external urls and explicit `href`s become plain anchors.
   *
   * In-page targets like "/#pricing" go through <Link>: a raw anchor triggers a
   * full document load, which reboots the whole single-page app just to move
   * down the page. ScrollToTop watches the hash and scrolls smoothly instead.
   */
  const isPlainAnchor = href || (to && to.startsWith('http'));

  if (isPlainAnchor) {
    const target = href ?? to;
    const external = target.startsWith('http');
    return (
      <a
        href={target}
        className={classes}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...props}
      >
        {children}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={classes} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <button type="button" className={classes} {...props}>
      {children}
    </button>
  );
}
